import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "./ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Checkbox } from "./ui/checkbox"
import { Card, CardContent, CardFooter } from "./ui/card"
import { Badge } from "./ui/badge"
import { cn } from "../lib/utils"

import {
  Folder,
  File as FileIcon,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  Archive,
  Search,
  X,
  Eye,
  EyeOff,
  Lock,
  Share2,
  Grid,
  List as ListIcon,
  LayoutGrid,
  Filter,
  CheckSquare,
  Square,
  FolderOpen,
  Edit2,
  MoveRight,
  ArchiveRestore,
  MoreVertical,
  AlertTriangle
} from 'lucide-react';
import { formatFileSize, formatDate } from '../utils/helpers';
import fileService from '../services/fileService';
import RenameDialog from './RenameDialog';
import MoveFileDialog from './MoveFileDialog';
import ShareFileDialog from './ShareFileDialog';
import ShareFolderDialog from './ShareFolderDialog';

const FileList = ({
  files,
  folders,
  onFileDelete,
  onFolderDelete,
  onFileDownload,
  onFolderDownload,
  onFolderOpen,
  onPdfView,
  onImageView,
  onFileShare,
  onFolderShare,
  onFileRename,
  onFolderRename,
  onFileMove,
  currentFolderId,
  defaultViewMode = 'compact'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState(defaultViewMode);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  // Bulk selection
  const [selectedItems, setSelectedItems] = useState([]);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [renameType, setRenameType] = useState(null);

  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [fileToMove, setFileToMove] = useState(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [itemToShare, setItemToShare] = useState(null);
  const [shareType, setShareType] = useState(null); // 'file' or 'folder'

  // ============================================
  // SELECTION HANDLERS
  // ============================================

  // Helper to get unique key
  const getItemKey = (item, type) => `${type}-${item.id}`;

  const handleSelectItem = (item, type) => {
    const key = getItemKey(item, type);
    setSelectedItems(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const isItemSelected = (item, type) => selectedItems.includes(getItemKey(item, type));

  const handleSelectAll = (checked) => {
    if (checked) {
      const allKeys = [
        ...filteredFolders.map(f => getItemKey(f, 'folder')),
        ...filteredFiles.map(f => getItemKey(f, 'file'))
      ];
      setSelectedItems(allKeys);
    } else {
      setSelectedItems([]);
    }
  };

  const handleClearSelection = () => setSelectedItems([]);

  // ============================================
  // HELPERS
  // ============================================

  const isPdfFile = (mime) => mime?.includes('pdf');
  const isImageFile = (mime) => mime?.startsWith('image/');

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <FileIcon className="h-5 w-5 text-gray-500" />;

    if (mimeType.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (mimeType.includes('word') || mimeType.includes('document'))
      return <FileText className="h-5 w-5 text-blue-500" />;
    if (mimeType.startsWith('image')) return <ImageIcon className="h-5 w-5 text-emerald-500" />;
    if (mimeType.startsWith('video')) return <Video className="h-5 w-5 text-amber-500" />;
    if (mimeType.startsWith('audio')) return <Music className="h-5 w-5 text-purple-500" />;
    if (mimeType.includes('zip') || mimeType.includes('rar'))
      return <Archive className="h-5 w-5 text-stone-500" />;

    return <FileIcon className="h-5 w-5 text-gray-500" />;
  };

  const getFileTypeLabel = (mimeType) => {
    if (!mimeType) return 'File';
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('word')) return 'Word';
    if (mimeType.includes('sheet')) return 'Excel';
    if (mimeType.startsWith('image')) return 'Image';
    if (mimeType.startsWith('video')) return 'Video';
    if (mimeType.startsWith('audio')) return 'Audio';
    if (mimeType.includes('zip')) return 'Archive';
    return 'File';
  };

  const getFileTypeColor = (mimeType) => {
    if (!mimeType) return 'bg-gray-100 text-gray-700 border-gray-200';
    if (mimeType.includes('pdf')) return 'bg-red-50 text-red-600 border-red-200';
    if (mimeType.includes('word')) return 'bg-blue-50 text-blue-600 border-blue-200';
    if (mimeType.startsWith('image')) return 'bg-emerald-50 text-emerald-600 border-emerald-200';
    if (mimeType.startsWith('video')) return 'bg-amber-50 text-amber-600 border-amber-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffDays = Math.floor((now - then) / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(date);
  };

  // ============================================
  // FILTER & SORT
  // ============================================

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.original_name.toLowerCase().includes(searchQuery.toLowerCase());

    if (filterType === 'all') return matchesSearch;
    if (filterType === 'pdf') return matchesSearch && isPdfFile(file.mime_type);
    if (filterType === 'image') return matchesSearch && isImageFile(file.mime_type);
    if (filterType === 'document') return matchesSearch &&
      (file.mime_type?.includes('word') || file.mime_type?.includes('document'));

    return matchesSearch;
  }).sort((a, b) => {
    if (sortBy === 'name') return a.original_name.localeCompare(b.original_name);
    if (sortBy === 'date') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'size') return b.file_size - a.file_size;
    return 0;
  });

  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Flat combined list for pagination (folders first, then files)
  const allItems = [
    ...filteredFolders.map(f => ({ ...f, _type: 'folder' })),
    ...filteredFiles.map(f => ({ ...f, _type: 'file' })),
  ];
  const allCount = allItems.length;
  const totalPages = Math.max(1, Math.ceil(allCount / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedItems = allItems.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);
  const pagedFolders = pagedItems.filter(i => i._type === 'folder');
  const pagedFiles = pagedItems.filter(i => i._type === 'file');

  const handleSearchChange = (val) => { setSearchQuery(val); setCurrentPage(1); };
  const handleFilterChange = (val) => { setFilterType(val); setCurrentPage(1); };
  const handleSortChange = (val) => { setSortBy(val); setCurrentPage(1); };

  const isAllSelected = allCount > 0 && selectedItems.length === allCount;

  // ============================================
  // ACTION HANDLERS
  // ============================================

  const handleDeleteClick = (item, type) => {
    setItemToDelete({ ...item, type });
    setDeleteDialogOpen(true);
  };

  const handleBulkDelete = () => {
    setItemToDelete({ isBulk: true, count: selectedItems.length });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete?.isBulk) {
      selectedItems.forEach(key => {
        const [type, id] = key.split('-');
        if (type === 'folder') onFolderDelete(id);
        else onFileDelete(id);
      });
      setSelectedItems([]);
    } else {
      if (itemToDelete.type === 'folder') onFolderDelete(itemToDelete.id);
      else onFileDelete(itemToDelete.id);
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDownload = (file) => {
    if (onFileDownload) {
      onFileDownload(file);
    }
  };

  const handleShareClick = (item, type) => {
    setItemToShare(item);
    setShareType(type);
    setShareDialogOpen(true);

    // Optional: still call parent handlers if provided, for auditing or other side effects
    if (type === 'file' && onFileShare) {
      onFileShare(item);
    } else if (type === 'folder' && onFolderShare) {
      onFolderShare(item);
    }
  };

  // Re-map the props to local handlers if they were just triggers in original
  // Actually, to avoid breaking changes, let's assume `onFileShare` prop might be passed.
  // But wait, in the original code, `onFileShare` was passed down.
  // If I want to be self-contained for dialogs (which is cleaner), I should use the imported dialogs.
  // Checking `RenameDialog` usage in original: it was imported and used.
  // Checking `ShareFileDialog`: it was NOT imported in original.
  // So previously Dashboard handled Share. I will now handle it HERE to colocate logic, reusing the components I just refactored.
  // This means `onFileShare` and `onFolderShare` props might become redundant if I handle it here, 
  // OR the parent passes the API calls.
  // Let's assume the components `ShareFileDialog` handle the API calls internally (they do, taking `file` as prop).

  const handleRenameClick = (item, type) => {
    setItemToRename(item);
    setRenameType(type);
    setRenameDialogOpen(true);
  };

  const handleMoveClick = (file) => {
    setFileToMove(file);
    setMoveDialogOpen(true);
  };

  // ============================================
  // DEPARTMENT VISIBILITY TOGGLE HANDLER
  // ============================================
  const [visibilityLoading, setVisibilityLoading] = useState(null); // menyimpan fileId yang sedang diproses

  const handleToggleDepartmentVisibility = async (file) => {
    if (visibilityLoading === file.id) return;
    setVisibilityLoading(file.id);
    try {
      const newVal = !(file.is_department_visible === 1 || file.is_department_visible === true);
      await fileService.toggleDepartmentVisibility(file.id, newVal);
      // Update lokal langsung tanpa reload penuh
      if (onFileRename) {
        // Pinjam callback rename hanya untuk trigger refresh parent
        // Idealnya ada callback onRefresh, tapi kita trigger via rename callback
      }
      // Trigger parent refresh jika ada
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('dept-visibility-changed', { detail: { fileId: file.id, isVisible: newVal } }));
      }
      // Optimistic update: mutasi object di array files via workaround
      file.is_department_visible = newVal ? 1 : 0;
    } catch (err) {
      console.error('Toggle visibility error:', err);
    } finally {
      setVisibilityLoading(null);
    }
  };

  // ============================================
  // RENDERERS
  // ============================================

  if (allCount === 0) {
    return (
      <div className="space-y-4">
        {/* Header content even when empty (search, filter) */}
        <div className="bg-white p-4 rounded-lg border shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <Folder className="h-6 w-6 text-blue-500" />
              <h2 className="text-lg font-semibold">File & Folder</h2>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:min-w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Cari file..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {/* Filters */}
              <div className="flex gap-2">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[110px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="image">Gambar</SelectItem>
                    <SelectItem value="document">Dokumen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className="bg-white p-12 text-center rounded-lg border border-dashed flex flex-col items-center justify-center">
          <div className="bg-gray-50 p-4 rounded-full mb-4">
            <FileIcon className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">Tidak ada file ditemukan</h3>
          <p className="text-gray-500 text-sm mt-1">
            {searchQuery ? 'Coba cari dengan kata kunci lain' : 'Upload file pertama Anda untuk memulai'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Toolbar */}
      <div className="bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Title & Bulk Select Checkbox (Responsive) */}
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center justify-center">
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={handleSelectAll}
                id="select-all"
              />
            </div>
            <div className="flex items-center gap-2">
              <Folder className="h-6 w-6 text-blue-500" />
              <h2 className="text-lg font-semibold hidden sm:block">File & Folder</h2>
              <span className="text-lg font-semibold sm:hidden">Files</span>
            </div>
          </div>

          {/* Actions & Filters */}
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <div className="relative flex-1 sm:min-w-[240px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Cari..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
              <Select value={filterType} onValueChange={handleFilterChange}>
                <SelectTrigger className="h-9 min-w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="image">Gambar</SelectItem>
                  <SelectItem value="document">Dokumen</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="h-9 min-w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nama</SelectItem>
                  <SelectItem value="date">Tanggal</SelectItem>
                  <SelectItem value="size">Ukuran</SelectItem>
                </SelectContent>
              </Select>

              {/* View Toggles */}
              <div className="flex items-center border rounded-md bg-gray-50 p-1 gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === 'list' ? 'white' : 'ghost'}
                        size="sm"
                        className={cn("h-7 w-7 p-0", viewMode === 'list' && "bg-white shadow-sm")}
                        onClick={() => setViewMode('list')}
                      >
                        <ListIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>List View</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === 'compact' ? 'white' : 'ghost'}
                        size="sm"
                        className={cn("h-7 w-7 p-0", viewMode === 'compact' && "bg-white shadow-sm")}
                        onClick={() => setViewMode('compact')}
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Compact View</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === 'grid' ? 'white' : 'ghost'}
                        size="sm"
                        className={cn("h-7 w-7 p-0", viewMode === 'grid' && "bg-white shadow-sm")}
                        onClick={() => setViewMode('grid')}
                      >
                        <Grid className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Grid View</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedItems.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg flex items-center justify-between animate-in slide-in-from-top-2">
          <div className="flex items-center gap-2 text-blue-700 font-medium">
            <CheckSquare className="h-5 w-5" />
            <span>{selectedItems.length} item dipilih</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleClearSelection} className="h-8 bg-white hover:bg-blue-50 text-blue-700 border-blue-200">
              Batalkan
            </Button>
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} className="h-8">
              <Trash2 className="h-4 w-4 mr-2" />
              Hapus
            </Button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="min-h-[300px]">

        {/* LIST VIEW */}
        {viewMode === 'list' && (
          <div className="rounded-md border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/50">
                  <TableHead className="w-[40%]">Nama</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Ukuran</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Folders */}
                {pagedFolders.map(folder => (
                  <TableRow
                    key={`folder-${folder.id}`}
                    className={cn(
                      "cursor-pointer hover:bg-gray-50",
                      isItemSelected(folder, 'folder') && "bg-blue-50/50 hover:bg-blue-50"
                    )}
                    onClick={() => onFolderOpen(folder)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 bg-amber-100 rounded-lg flex items-center justify-center text-amber-500">
                          <Folder className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{folder.name}</span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <FolderOpen className="h-3 w-3" />
                            {folder.item_count || 0} items
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        Folder
                      </div>
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell className="text-gray-500">{formatTimeAgo(folder.created_at)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-500 hover:bg-amber-50" onClick={() => handleShareClick(folder, 'folder')}>
                                <Share2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Bagikan</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:bg-blue-50" onClick={() => handleRenameClick(folder, 'folder')}>
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Rename</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => handleDeleteClick(folder, 'folder')}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Hapus</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleSelectItem(folder, 'folder')}
                        >
                          <Checkbox checked={isItemSelected(folder, 'folder')} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Files */}
                {pagedFiles.map(file => {
                  const typeDetails = getFileTypeColor(file.mime_type);

                  return (
                    <TableRow
                      key={`file-${file.id}`}
                      className={cn(
                        "hover:bg-gray-50",
                        isItemSelected(file, 'file') && "bg-blue-50/50 hover:bg-blue-50"
                      )}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", typeDetails.split(' ')[0])}>
                            {getFileIcon(file.mime_type)}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="font-medium text-gray-900 truncate max-w-[200px]">{file.original_name}</span>
                                </TooltipTrigger>
                                <TooltipContent>{file.original_name}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold", typeDetails)}>
                          {getFileTypeLabel(file.mime_type)}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{formatFileSize(file.file_size)}</TableCell>
                      <TableCell className="text-gray-500">{formatTimeAgo(file.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {/* Action Buttons */}
                          {(isPdfFile(file.mime_type) || isImageFile(file.mime_type)) && (
                            <Button
                              variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-blue-600"
                              onClick={() => isPdfFile(file.mime_type) ? onPdfView(file) : onImageView(file)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}

                          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-green-600" onClick={() => handleDownload(file)}>
                            <Download className="h-4 w-4" />
                          </Button>

                          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-amber-600" onClick={() => handleShareClick(file, 'file')}>
                            <Share2 className="h-4 w-4" />
                          </Button>

                          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-blue-600" onClick={() => handleRenameClick(file, 'file')}>
                            <Edit2 className="h-4 w-4" />
                          </Button>

                          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-purple-600" onClick={() => handleMoveClick(file)}>
                            <MoveRight className="h-4 w-4" />
                          </Button>

                          {/* Toggle Visibilitas Departemen (List View) */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-8 w-8",
                                    (file.is_department_visible === 1 || file.is_department_visible === true)
                                      ? "text-blue-500 hover:text-orange-600"
                                      : "text-orange-500 hover:text-blue-600"
                                  )}
                                  onClick={() => handleToggleDepartmentVisibility(file)}
                                  disabled={visibilityLoading === file.id}
                                >
                                  {(file.is_department_visible === 1 || file.is_department_visible === true)
                                    ? <Eye className="h-4 w-4" />
                                    : <Lock className="h-4 w-4" />
                                  }
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {(file.is_department_visible === 1 || file.is_department_visible === true)
                                  ? 'Tampil di departemen — klik untuk sembunyikan'
                                  : 'Rahasia (tersembunyi dari departemen) — klik untuk tampilkan'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-red-600" onClick={() => handleDeleteClick(file, 'file')}>
                            <Trash2 className="h-4 w-4" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleSelectItem(file, 'file')}
                          >
                            <Checkbox checked={isItemSelected(file, 'file')} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* COMPACT VIEW */}
        {viewMode === 'compact' && (
          <div className="space-y-2">
            {/* Folders */}
            {pagedFolders.map(folder => (
              <Card
                key={`folder-${folder.id}`}
                className={cn(
                  "cursor-pointer hover:border-blue-300 transition-all",
                  isItemSelected(folder, 'folder') && "border-blue-500 bg-blue-50"
                )}
                onClick={() => onFolderOpen(folder)}
              >
                <div className="flex items-center p-3 gap-3">
                  <div key="icon" className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center text-amber-500 shrink-0">
                    <Folder className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{folder.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 rounded">Folder</div>
                      <span className="text-xs text-gray-500">{folder.item_count || 0} items</span>
                      <span className="text-xs text-gray-400">• {formatTimeAgo(folder.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-amber-600 hover:bg-amber-50" onClick={() => handleShareClick(folder, 'folder')}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50" onClick={() => handleRenameClick(folder, 'folder')}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteClick(folder, 'folder')}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Checkbox
                      checked={isItemSelected(folder, 'folder')}
                      onCheckedChange={() => handleSelectItem(folder, 'folder')}
                      className="mx-1"
                    />
                  </div>
                </div>
              </Card>
            ))}

            {/* Files */}
            {pagedFiles.map(file => {
              const typeDetails = getFileTypeColor(file.mime_type);
              return (
                <Card
                  key={`file-${file.id}`}
                  className={cn(
                    "hover:border-blue-300 transition-all group",
                    isItemSelected(file, 'file') && "border-blue-500 bg-blue-50"
                  )}
                >
                  <div className="flex items-center p-3 gap-3 relative">
                    <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center shrink-0", typeDetails.split(' ')[0])}>
                      {getFileIcon(file.mime_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{file.original_name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={cn("text-xs font-semibold border px-1.5 rounded", typeDetails)}>{getFileTypeLabel(file.mime_type)}</div>
                        <span className="text-xs text-gray-500 font-mono">{formatFileSize(file.file_size)}</span>
                        <span className="text-xs text-gray-400">• {formatTimeAgo(file.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {(isPdfFile(file.mime_type) || isImageFile(file.mime_type)) && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600" onClick={() => isPdfFile(file.mime_type) ? onPdfView(file) : onImageView(file)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-green-600" onClick={() => onFileDownload(file)}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-amber-600" onClick={() => handleShareClick(file, 'file')}>
                        <Share2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600" onClick={() => handleRenameClick(file, 'file')}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-purple-600" onClick={() => handleMoveClick(file)}>
                        <MoveRight className="h-4 w-4" />
                      </Button>
                      {/* Toggle Visibilitas Departemen (Compact View) */}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-8 w-8",
                                (file.is_department_visible === 1 || file.is_department_visible === true)
                                  ? "text-blue-400 hover:text-orange-600"
                                  : "text-orange-500 hover:text-blue-600"
                              )}
                              onClick={() => handleToggleDepartmentVisibility(file)}
                              disabled={visibilityLoading === file.id}
                            >
                              {(file.is_department_visible === 1 || file.is_department_visible === true)
                                ? <Eye className="h-4 w-4" />
                                : <Lock className="h-4 w-4" />
                              }
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {(file.is_department_visible === 1 || file.is_department_visible === true)
                              ? 'Tampil di departemen — klik untuk sembunyikan'
                              : 'Rahasia (tersembunyi dari departemen) — klik untuk tampilkan'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" onClick={() => handleDeleteClick(file, 'file')}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Checkbox
                        checked={isItemSelected(file, 'file')}
                        onCheckedChange={() => handleSelectItem(file, 'file')}
                        className="mx-1"
                      />
                    </div>
                    <div className="opacity-100 group-hover:opacity-0 absolute right-4 top-1/2 -translate-y-1/2 transition-opacity">
                      <Checkbox
                        checked={isItemSelected(file, 'file')}
                        onCheckedChange={() => handleSelectItem(file, 'file')}
                      />
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {/* GRID VIEW */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Folders */}
            {pagedFolders.map(folder => (
              <Card
                key={`folder-${folder.id}`}
                className={cn(
                  "cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all duration-200",
                  isItemSelected(folder, 'folder') && "ring-2 ring-blue-500 bg-blue-50"
                )}
                onClick={() => onFolderOpen(folder)}
              >
                <CardContent className="p-4 flex flex-col items-center text-center gap-3">
                  <div className="h-14 w-14 bg-amber-100 rounded-xl flex items-center justify-center text-amber-500 shadow-sm">
                    <Folder className="h-8 w-8" />
                  </div>
                  <div className="w-full">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <h4 className="font-semibold text-gray-900 truncate">{folder.name}</h4>
                        </TooltipTrigger>
                        <TooltipContent>{folder.name}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <p className="text-xs text-gray-500 mt-1">{folder.item_count || 0} items</p>
                    <p className="text-[10px] text-gray-400 mt-1">{formatTimeAgo(folder.created_at)}</p>
                  </div>

                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-amber-600" onClick={() => handleShareClick(folder, 'folder')}>
                      <Share2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-blue-600" onClick={() => handleRenameClick(folder, 'folder')}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-600" onClick={() => handleDeleteClick(folder, 'folder')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    <div className="flex items-center justify-center w-7">
                      <Checkbox
                        checked={isItemSelected(folder, 'folder')}
                        onCheckedChange={() => handleSelectItem(folder, 'folder')}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Files */}
            {pagedFiles.map(file => {
              const typeDetails = getFileTypeColor(file.mime_type);
              return (
                <Card
                  key={`file-${file.id}`}
                  className={cn(
                    "hover:shadow-md hover:-translate-y-1 transition-all duration-200",
                    isItemSelected(file, 'file') && "ring-2 ring-blue-500 bg-blue-50"
                  )}
                >
                  <CardContent className="p-4 flex flex-col items-center text-center gap-3 relative group">
                    <div className={cn("h-14 w-14 rounded-xl flex items-center justify-center shadow-sm", typeDetails.split(' ')[0])}>
                      {getFileIcon(file.mime_type)}
                    </div>
                    <div className="w-full">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <h4 className="font-semibold text-gray-900 truncate">{file.original_name}</h4>
                          </TooltipTrigger>
                          <TooltipContent>{file.original_name}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <p className="text-xs text-gray-500 mt-1 font-mono">{formatFileSize(file.file_size)}</p>
                      <div className={cn("mt-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border", typeDetails)}>
                        {getFileTypeLabel(file.mime_type)}
                      </div>
                    </div>

                    {/* Absolute positioned checkboxes for cleaner grid */}
                    <div className="absolute top-2 right-2">
                      <Checkbox
                        checked={isItemSelected(file, 'file')}
                        onCheckedChange={() => handleSelectItem(file, 'file')}
                      />
                    </div>

                    {/* Action Overlay on Hover */}
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-white via-white to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex justify-center gap-1">
                      {(isPdfFile(file.mime_type) || isImageFile(file.mime_type)) && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-blue-600 bg-white shadow-sm border" onClick={() => isPdfFile(file.mime_type) ? onPdfView(file) : onImageView(file)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-green-600 bg-white shadow-sm border" onClick={() => onFileDownload(file)}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-amber-600 bg-white shadow-sm border" onClick={() => handleShareClick(file, 'file')}>
                        <Share2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-500 hover:text-red-600 bg-white shadow-sm border" onClick={() => handleDeleteClick(file, 'file')}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* ===== PAGINATION BAR ===== */}
      {totalPages > 1 && (
        <div className="bg-white border rounded-lg px-4 py-3 flex items-center gap-3">
          {/* Navigation controls — LEFT side, safe from FAB */}
          <div className="flex items-center gap-1">
            <button
              disabled={safePage === 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              style={{ height: 32, width: 32, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: safePage === 1 ? 'not-allowed' : 'pointer', opacity: safePage === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#374151', fontWeight: 500 }}
            >‹</button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
              const show = page === 1 || page === totalPages || Math.abs(page - safePage) <= 1;
              const isEllipsisBefore = page === safePage - 2 && safePage > 3;
              const isEllipsisAfter = page === safePage + 2 && safePage < totalPages - 2;
              if (isEllipsisBefore || isEllipsisAfter) return (
                <span key={page} style={{ height: 32, width: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>…</span>
              );
              if (!show) return null;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    height: 32, width: 32, borderRadius: 6, border: '1px solid', cursor: 'pointer',
                    borderColor: page === safePage ? '#3b82f6' : '#e2e8f0',
                    background: page === safePage ? '#3b82f6' : '#fff',
                    color: page === safePage ? '#fff' : '#374151',
                    fontWeight: 600, fontSize: 13,
                    boxShadow: page === safePage ? '0 1px 4px rgba(59,130,246,0.3)' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >{page}</button>
              );
            })}

            <button
              disabled={safePage === totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              style={{ height: 32, width: 32, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', opacity: safePage === totalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#374151', fontWeight: 500 }}
            >›</button>
          </div>

          {/* Item info — right side */}
          <span className="text-sm text-gray-500 ml-auto">
            Hal. <span className="font-semibold text-gray-700">{safePage}</span> dari{' '}
            <span className="font-semibold text-gray-700">{totalPages}</span>
            &nbsp;·&nbsp;
            <span className="font-semibold text-gray-700">{allCount}</span> item
          </span>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Konfirmasi Hapus
            </DialogTitle>
            <DialogDescription>
              Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {itemToDelete?.isBulk ? (
              <p>Apakah Anda yakin ingin menghapus <strong>{itemToDelete.count} item yang dipilih</strong>?</p>
            ) : (
              <p>Apakah Anda yakin ingin menghapus <strong>{itemToDelete?.type === 'folder' ? itemToDelete?.name : itemToDelete?.original_name}</strong>?</p>
            )}

            {(itemToDelete?.type === 'folder' || (itemToDelete?.isBulk && selectedItems.some(k => k.startsWith('folder')))) && (
              <div className="mt-3 bg-red-50 p-3 rounded-md border border-red-200">
                <p className="text-sm text-red-700 font-medium">
                  ⚠️ Peringatan: Menghapus folder akan menghapus semua file di dalamnya.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Batal</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>Ya, Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Other Dialogs */}
      <RenameDialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        onRename={(newName) => {
          if (renameType === 'folder') onFolderRename(itemToRename.id, newName);
          else onFileRename(itemToRename.id, newName);
          setRenameDialogOpen(false);
        }}
        itemType={renameType}
        currentName={itemToRename?.name || itemToRename?.original_name || ''}
      />

      <MoveFileDialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        onMove={(folderId) => {
          if (fileToMove) onFileMove(fileToMove.id, folderId, fileToMove.original_name);
          setMoveDialogOpen(false);
        }}
        file={fileToMove}
        currentFolderId={currentFolderId}
      />

      {/* Share Dialogs */}
      {/* We conditionally render based on type to avoid null props issues */}
      <ShareFileDialog
        open={shareDialogOpen && shareType === 'file'}
        onClose={() => setShareDialogOpen(false)}
        file={itemToShare}
      />

      <ShareFolderDialog
        open={shareDialogOpen && shareType === 'folder'}
        onClose={() => setShareDialogOpen(false)}
        folder={itemToShare}
      />

    </div>
  );
};

export default FileList;